import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, SaveIcon, GitBranchIcon, BookOpenIcon, PlusCircleIcon, RefreshCwIcon, TagIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { RuleConditionNode } from './RuleConditionNode';
import { LogicGroupBlock } from './LogicGroupBlock';
import { ActionCard } from './ActionCard';
import { FDOARuleEditor } from './FDOARuleEditor';
export function RuleSetEditor({
  ruleSet,
  modules,
  triggerEvents,
  reusableConditions,
  onSave,
  locale
}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    module: '',
    triggerEvent: '',
    status: 'Draft',
    version: '0.1',
    conditions: [],
    actions: []
  });
  const [availableTriggers, setAvailableTriggers] = useState([]);
  const [errors, setErrors] = useState({});
  // Add a new state to track if we're editing an FDOA rule
  const [isFDOARule, setIsFDOARule] = useState(ruleSet?.ruleType === 'Financial Delegation' || false);
  // Initialize form data when rule set changes
  useEffect(() => {
    if (ruleSet) {
      setFormData({
        ...ruleSet
      });
      if (ruleSet.module) {
        setAvailableTriggers(triggerEvents[ruleSet.module] || []);
      }
    } else {
      setFormData({
        id: '',
        name: '',
        description: '',
        module: '',
        triggerEvent: '',
        status: 'Draft',
        version: '0.1',
        conditions: [],
        actions: []
      });
    }
  }, [ruleSet, triggerEvents]);
  // Handle form field changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
    // Update available triggers when module changes
    if (name === 'module') {
      setAvailableTriggers(triggerEvents[value] || []);
      setFormData({
        ...formData,
        module: value,
        triggerEvent: ''
      });
    }
  };
  // Add a root condition group if none exists
  const addRootCondition = () => {
    if (formData.conditions.length === 0) {
      setFormData({
        ...formData,
        conditions: [{
          id: `cond-${new Date().getTime()}`,
          type: 'group',
          operator: 'AND',
          children: []
        }]
      });
    }
  };
  // Add a condition to a group
  const addCondition = groupId => {
    const newCondition = {
      id: `cond-${new Date().getTime()}`,
      type: 'condition',
      field: '',
      operator: 'equals',
      value: ''
    };
    const updateConditions = conditions => {
      return conditions.map(condition => {
        if (condition.id === groupId) {
          return {
            ...condition,
            children: [...condition.children, newCondition]
          };
        } else if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: updateConditions(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: updateConditions(formData.conditions)
    });
  };
  // Add a nested group to a parent group
  const addNestedGroup = (parentGroupId, operator = 'AND') => {
    const newGroup = {
      id: `cond-${new Date().getTime()}`,
      type: 'group',
      operator,
      children: []
    };
    const updateConditions = conditions => {
      return conditions.map(condition => {
        if (condition.id === parentGroupId) {
          return {
            ...condition,
            children: [...condition.children, newGroup]
          };
        } else if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: updateConditions(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: updateConditions(formData.conditions)
    });
  };
  // Update a condition
  const updateCondition = (conditionId, updates) => {
    const updateConditionInArray = conditions => {
      return conditions.map(condition => {
        if (condition.id === conditionId) {
          return {
            ...condition,
            ...updates
          };
        } else if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: updateConditionInArray(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: updateConditionInArray(formData.conditions)
    });
  };
  // Remove a condition or group
  const removeCondition = conditionId => {
    const removeConditionFromArray = conditions => {
      return conditions.filter(condition => condition.id !== conditionId).map(condition => {
        if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: removeConditionFromArray(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: removeConditionFromArray(formData.conditions)
    });
  };
  // Add a new action
  const addAction = () => {
    const newAction = {
      id: `action-${new Date().getTime()}`,
      type: 'notify',
      target: '',
      targetType: 'email',
      template: ''
    };
    setFormData({
      ...formData,
      actions: [...formData.actions, newAction]
    });
  };
  // Update an action
  const updateAction = (actionId, updates) => {
    setFormData({
      ...formData,
      actions: formData.actions.map(action => action.id === actionId ? {
        ...action,
        ...updates
      } : action)
    });
  };
  // Remove an action
  const removeAction = actionId => {
    setFormData({
      ...formData,
      actions: formData.actions.filter(action => action.id !== actionId)
    });
  };
  // Add a reusable condition
  const addReusableCondition = (groupId, conditionTemplate) => {
    const newCondition = {
      ...conditionTemplate.condition,
      id: `cond-${new Date().getTime()}`
    };
    const updateConditions = conditions => {
      return conditions.map(condition => {
        if (condition.id === groupId) {
          return {
            ...condition,
            children: [...condition.children, newCondition]
          };
        } else if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: updateConditions(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: updateConditions(formData.conditions)
    });
  };
  // Toggle the operator of a group (AND/OR)
  const toggleGroupOperator = groupId => {
    const updateConditions = conditions => {
      return conditions.map(condition => {
        if (condition.id === groupId) {
          return {
            ...condition,
            operator: condition.operator === 'AND' ? 'OR' : 'AND'
          };
        } else if (condition.type === 'group' && condition.children) {
          return {
            ...condition,
            children: updateConditions(condition.children)
          };
        }
        return condition;
      });
    };
    setFormData({
      ...formData,
      conditions: updateConditions(formData.conditions)
    });
  };
  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) {
      newErrors.name = 'Rule set name is required';
    }
    if (!formData.module) {
      newErrors.module = 'Module is required';
    }
    if (!formData.triggerEvent) {
      newErrors.triggerEvent = 'Trigger event is required';
    }
    if (formData.conditions.length === 0) {
      newErrors.conditions = 'At least one condition is required';
    } else {
      // Check if any condition group is empty
      const hasEmptyGroup = conditions => {
        for (const condition of conditions) {
          if (condition.type === 'group') {
            if (condition.children.length === 0) {
              return true;
            }
            if (hasEmptyGroup(condition.children)) {
              return true;
            }
          }
        }
        return false;
      };
      if (hasEmptyGroup(formData.conditions)) {
        newErrors.conditions = 'Condition groups cannot be empty';
      }
      // Check if any condition is incomplete
      const hasIncompleteCondition = conditions => {
        for (const condition of conditions) {
          if (condition.type === 'condition') {
            if (!condition.field || !condition.operator || condition.value === undefined || condition.value === '') {
              return true;
            }
          } else if (condition.type === 'group') {
            if (hasIncompleteCondition(condition.children)) {
              return true;
            }
          }
        }
        return false;
      };
      if (hasIncompleteCondition(formData.conditions)) {
        newErrors.conditions = 'All conditions must be complete';
      }
    }
    if (formData.actions.length === 0) {
      newErrors.actions = 'At least one action is required';
    } else {
      // Check if any action is incomplete
      const hasIncompleteAction = formData.actions.some(action => {
        if (action.type === 'notify' || action.type === 'route') {
          return !action.target || !action.targetType;
        }
        return false;
      });
      if (hasIncompleteAction) {
        newErrors.actions = 'All actions must be complete';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  // Handle Rule Type Change
  const handleRuleTypeChange = e => {
    const newRuleType = e.target.value;
    setIsFDOARule(newRuleType === 'Financial Delegation');
    setFormData({
      ...formData,
      ruleType: newRuleType
    });
  };
  // Handle Module Change
  const handleModuleChange = e => {
    const newModule = e.target.value;
    setFormData({
      ...formData,
      module: newModule,
      triggerEvent: ''
    });
    if (newModule) {
      setAvailableTriggers(triggerEvents[newModule] || []);
    }
  };
  // Handle Save
  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };
  // Render condition tree recursively
  const renderConditionTree = (conditions, level = 0) => {
    return conditions.map(condition => {
      if (condition.type === 'group') {
        return <LogicGroupBlock key={condition.id} condition={condition} level={level} onAddCondition={() => addCondition(condition.id)} onAddNestedGroup={operator => addNestedGroup(condition.id, operator)} onAddTemplate={template => addReusableCondition(condition.id, template)} onToggleOperator={() => toggleGroupOperator(condition.id)} onRemove={() => removeCondition(condition.id)} reusableConditions={reusableConditions} data-id="element-11454">
            {condition.children && condition.children.length > 0 ? renderConditionTree(condition.children, level + 1) : <div className="p-4 text-center text-sm text-gray-500" data-id="element-11455">
                No conditions in this group. Add one below.
              </div>}
          </LogicGroupBlock>;
      } else {
        return <RuleConditionNode key={condition.id} condition={condition} onChange={updates => updateCondition(condition.id, updates)} onRemove={() => removeCondition(condition.id)} data-id="element-11456" />;
      }
    });
  };
  return <div className="space-y-6" data-id="element-11457">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11458">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11459">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11460">
            {ruleSet ? 'Edit Rule Set' : 'Create Rule Set'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-11461">
            {ruleSet ? 'Update the rule set details below' : 'Configure a new rule set for business process automation'}
          </p>
        </div>
        {/* Basic Information */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-11462">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-11463">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11464">
            <div className="sm:col-span-3" data-id="element-11465">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-11466">
                Rule Set Name
              </label>
              <div className="mt-1" data-id="element-11467">
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11468" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11469">
              <label htmlFor="module" className="block text-sm font-medium text-gray-700" data-id="element-11470">
                Module
              </label>
              <div className="mt-1" data-id="element-11471">
                <select id="module" name="module" value={formData.module} onChange={handleModuleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11472">
                  <option value="" data-id="element-11473">Select Module</option>
                  {modules.map(module => <option key={module.id} value={module.id} data-id="element-11474">
                      {module.name}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11475">
              <label htmlFor="triggerEvent" className="block text-sm font-medium text-gray-700" data-id="element-11476">
                Trigger Event
              </label>
              <div className="mt-1" data-id="element-11477">
                <select id="triggerEvent" name="triggerEvent" value={formData.triggerEvent} onChange={handleInputChange} disabled={!formData.module} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${!formData.module ? 'bg-gray-100' : ''}`} data-id="element-11478">
                  <option value="" data-id="element-11479">Select Trigger Event</option>
                  {formData.module && triggerEvents[formData.module]?.map(event => <option key={event.id} value={event.id} data-id="element-11480">
                        {event.name}
                      </option>)}
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11481">
              <label htmlFor="ruleType" className="block text-sm font-medium text-gray-700" data-id="element-11482">
                Rule Type
              </label>
              <div className="mt-1" data-id="element-11483">
                <select id="ruleType" name="ruleType" value={formData.ruleType || ''} onChange={handleRuleTypeChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11484">
                  <option value="" data-id="element-11485">Standard Rule</option>
                  <option value="Financial Delegation" data-id="element-11486">
                    Financial Delegation
                  </option>
                  <option value="Compliance" data-id="element-11487">Compliance Rule</option>
                  <option value="Routing" data-id="element-11488">Routing Rule</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-11489">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-11490">
                Description
              </label>
              <div className="mt-1" data-id="element-11491">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11492" />
              </div>
            </div>
          </div>
        </div>
        {/* Render FDOA Rule Editor if it's an FDOA rule type */}
        {isFDOARule ? <FDOARuleEditor rule={ruleSet || formData} onSave={updatedRule => {
        const finalRule = {
          ...formData,
          ...updatedRule,
          ruleType: 'Financial Delegation'
        };
        onSave(finalRule);
      }} onCancel={() => setIsFDOARule(false)} locale={locale} data-id="element-11493" /> : <>
            {/* Conditions Section */}
            <div className="sm:col-span-6" data-id="element-11494">
              <div className="flex justify-between items-center mb-2" data-id="element-11495">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-11496">
                  Conditions
                </h3>
                {formData.conditions.length === 0 && <button type="button" onClick={addRootCondition} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11497">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-11498" />
                    Add Condition Group
                  </button>}
              </div>
              {errors.conditions && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" data-id="element-11499">
                  <div className="flex" data-id="element-11500">
                    <AlertTriangleIcon className="h-5 w-5 text-red-400 mr-2" data-id="element-11501" />
                    <p className="text-sm text-red-600" data-id="element-11502">{errors.conditions}</p>
                  </div>
                </div>}
              <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-11503">
                {formData.conditions.length > 0 ? <div className="divide-y divide-gray-200" data-id="element-11504">
                    {renderConditionTree(formData.conditions)}
                  </div> : <div className="p-6 text-center" data-id="element-11505">
                    <GitBranchIcon className="mx-auto h-10 w-10 text-gray-400" data-id="element-11506" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-11507">
                      No conditions
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-11508">
                      Start by adding a condition group to build your rule
                      logic.
                    </p>
                    <div className="mt-6" data-id="element-11509">
                      <button type="button" onClick={addRootCondition} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11510">
                        <PlusIcon className="h-4 w-4 mr-2" data-id="element-11511" />
                        Add Condition Group
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
            {/* Actions Section */}
            <div className="sm:col-span-6" data-id="element-11512">
              <div className="flex justify-between items-center mb-2" data-id="element-11513">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-11514">Actions</h3>
                <button type="button" onClick={addAction} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11515">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-11516" />
                  Add Action
                </button>
              </div>
              {errors.actions && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" data-id="element-11517">
                  <div className="flex" data-id="element-11518">
                    <AlertTriangleIcon className="h-5 w-5 text-red-400 mr-2" data-id="element-11519" />
                    <p className="text-sm text-red-600" data-id="element-11520">{errors.actions}</p>
                  </div>
                </div>}
              <div className="space-y-3" data-id="element-11521">
                {formData.actions.length > 0 ? formData.actions.map((action, index) => <ActionCard key={action.id} action={action} index={index} onChange={updates => updateAction(action.id, updates)} onRemove={() => removeAction(action.id)} data-id="element-11522" />) : <div className="p-6 text-center border border-gray-200 rounded-md" data-id="element-11523">
                    <CheckCircleIcon className="mx-auto h-10 w-10 text-gray-400" data-id="element-11524" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-11525">
                      No actions
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-11526">
                      Add actions to execute when the conditions are met.
                    </p>
                    <div className="mt-6" data-id="element-11527">
                      <button type="button" onClick={addAction} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11528">
                        <PlusIcon className="h-4 w-4 mr-2" data-id="element-11529" />
                        Add Action
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </>}
        {/* Only show save button for non-FDOA rules since FDOA has its own buttons */}
        {!isFDOARule && <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" data-id="element-11530">
            <button type="button" onClick={handleSave} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11531">
              Save
            </button>
          </div>}
      </div>
    </div>;
}